import React from 'react';
import { TargetIcon } from 'lucide-react';
import { Input } from '../ui/Input';
import { Checkbox } from '../ui/Checkbox';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
import { LandingMetaPixel, MetaPixelEvents } from '../../types';
import { META_PIXEL_EVENT_INFO } from '../../data/metaPixel';
import { isValidPixelId } from '../../utils/metaPixel';

/**
 * Panel «Meta Pixel» del constructor, por landing.
 *
 * Por defecto cada landing usa el Pixel global de la organización
 * (Configuración → Integraciones → Meta Pixel). Aquí solo se decide si ESTA
 * landing debe sobrescribirlo con un Pixel propio; el resto de la
 * configuración (activar/desactivar, Pixel ID, eventos) vive en el mismo
 * objeto `pageConfig.metaPixel`, así que viaja con el guardado y la
 * publicación como cualquier otro ajuste de la landing.
 */

interface MetaPixelSettingsProps {
  value?: LandingMetaPixel;
  onChange: (config: LandingMetaPixel) => void;
  /** Valor actual del Pixel global de la organización, para poder resincronizar. */
  orgDefault?: {
    enabled: boolean;
    pixelId?: string;
    events?: MetaPixelEvents;
  };
}
export function MetaPixelSettings({
  value,
  onChange,
  orgDefault
}: MetaPixelSettingsProps) {
  const config = value ?? {};
  const override = Boolean(config.override);
  const events = config.events ?? {};
  function setEvent(key: keyof MetaPixelEvents, checked: boolean): void {
    onChange({
      ...config,
      events: {
        ...events,
        [key]: checked
      }
    });
  }
  return <div className="mt-5 space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-433" data-mp="8tdsxaa2uxv7">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground" data-id="element-434" data-mp="3ukijqq37mz9">
        <TargetIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-435" data-mp="3v26ojq0a41ja" />
        Meta Pixel
      </h3>
      <Select id="pixel-source" label="Pixel a usar en esta landing" options={[{
      value: 'global',
      label: 'Usar el Pixel global de la organización'
    }, {
      value: 'own',
      label: 'Sobrescribir Pixel para esta landing'
    }]} value={override ? 'own' : 'global'} onChange={e => {
      const wantsOwn = e.target.value === 'own';
      if (wantsOwn) {
        // Al pasar a «propio» se conserva lo que ya hubiera (o se empieza en
        // blanco): no depende del Pixel global.
        onChange({
          ...config,
          override: true
        });
        return;
      }
      /*
       * Volver a «global» debe RESTAURAR de verdad el Pixel de la
       * organización, no solo cambiar la etiqueta. Sin esto, la landing se
       * quedaba con el Pixel ID propio anterior (resolveMetaPixel no mira
       * `override`, solo `enabled`/`pixelId`), así que seguía disparando el
       * Pixel equivocado aunque el selector dijera «global».
       */
      onChange({
        override: false,
        enabled: orgDefault?.enabled ?? false,
        pixelId: orgDefault?.pixelId,
        events: orgDefault?.events ?? events
      });
    }} data-id="element-436" data-mp="2oo7d0fe5ti73" />
      {override && <div className="space-y-3 rounded-xl border border-border p-4" data-id="element-437" data-mp="14tpg0stoyvk4">
          <p className="text-xs text-muted-foreground" data-id="element-438" data-mp="zu2zoaikgz73">
            Este Pixel sustituye al de la organización solo en esta landing.
          </p>
          <div className="flex items-center justify-between gap-3" data-id="element-439" data-mp="1es55ysgzfys1">
            <span className="text-sm font-medium text-foreground" data-id="element-440" data-mp="19qpk4y6zdo8l">Activar</span>
            <Switch id="pixel-own-enabled" label="Activar" checked={Boolean(config.enabled)} onChange={checked => onChange({
          ...config,
          enabled: checked
        })} data-id="element-441" data-mp="1nr0b018aj2tr" />
          </div>
          <Input id="pixel-own-id" label="Pixel ID" placeholder="123456789012345" value={config.pixelId ?? ''} onChange={e => onChange({
        ...config,
        pixelId: e.target.value.trim()
      })} data-id="element-442" data-mp="1xhuwv4hmk0eo" />
          {config.pixelId && !isValidPixelId(config.pixelId) && <p role="alert" className="text-xs text-red-600" data-id="element-443" data-mp="26fx8sf2wcdgq">
              Introduce un ID de Meta Pixel válido.
            </p>}
          <div data-id="element-444" data-mp="3vzeunsbogci8">
            <p className="mb-2 text-sm font-medium text-foreground" data-id="element-445" data-mp="3vzx0ngo5653c">Eventos activos</p>
            <div className="space-y-2" data-id="element-446" data-mp="4sar57eqr8ek">
              {META_PIXEL_EVENT_INFO.map(event => <div key={event.key} className={event.disabled ? 'opacity-60' : ''} data-id="element-447" data-mp="9jkge6wdo9px">
                  <Checkbox label={event.label} checked={event.key === 'pageView' ? events.pageView !== false : event.key === 'lead' ? events.lead !== false : event.key === 'whatsapp' ? events.whatsapp !== false : Boolean(events.schedule)} disabled={event.disabled} onChange={e => setEvent(event.key, e.target.checked)} data-id="element-448" data-mp="9lnd19s1d13m" />
                  <p className="ml-6 text-xs text-muted-foreground" data-id="element-449" data-mp="3myp4b456rebw">{event.description}</p>
                </div>)}
            </div>
          </div>
        </div>}
    </div>;
}